import React, { PropsWithChildren, ReactNode } from 'react';


import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { HtmlTrans, SectionHeader, Typography } from 'web-library';
import { Grid } from '@mui/material';

const useStyles = makeStyles()((theme) =>
({
  description: {
    marginBottom: theme.spacing(5),
  },
  sectionTitle: {
    marginBottom: theme.spacing(4),
  },
  submissionStepSubSectionContainer: {
    marginBottom: theme.spacing(4),
  },
  submissionStepSubSectionTitle: {
    color: theme.color.text.textHeader,
    fontWeight: 'bold',
  },
  disablilitiesSubheader: {
    color: theme.color.text.textHeader,
    marginBottom: theme.spacing(2),
  },
  disablilitiesDescription: {
    marginBottom: theme.spacing(2),
  },
  learningDisablilitiesBulletSectionContainer: {
    marginBottom: theme.spacing(3),
  },
  learningDisablilitiesBulletSectionHeader: {
    fontWeight: 'bold',
  },
  learningDisablilitiesBullets: {
    fontWeight: 'bold',
  },
  a: {
    textDecoration: 'none',
    color: theme.color.link.linkDefault01,
  },
}));

const unorderedList = (listItems: string[]) => (
  <ul>
    {listItems.map((listItem) => (
      <li key={listItem}>
        <Typography variant="body1">{listItem}</Typography>
      </li>
    ))}
  </ul>
);

const Section = (props: PropsWithChildren<{ title: string }>) => {
  const { classes } = useStyles();
  const { title, children } = props;
  return (
    <Grid className={classes.learningDisablilitiesBulletSectionContainer}>
      <Typography variant="h3" className={classes.sectionTitle}>
        {title}
      </Typography>
      {children}
    </Grid>
  );
};

const SubmissionStepSubSection = ({
  title,
  description,
}: {
  title: string;
  description: string | ReactNode;
}) => {
  const { classes } = useStyles();
  return (
    <Grid className={classes.submissionStepSubSectionContainer}>
      <Typography variant="body1" className={classes.submissionStepSubSectionTitle}>
        {title}
      </Typography>
      <br />
      <Typography variant="body1">{description}</Typography>
    </Grid>
  );
};

const LearningDisabilitiesBullets = (props: {
  title: string;
  bullets: ReactNode;
  footnote: string;
}) => {
  const { classes } = useStyles();
  const { title, bullets, footnote } = props;
  return (
    <Grid className={classes.learningDisablilitiesBulletSectionContainer}>
      <Typography variant="body1" className={classes.learningDisablilitiesBulletSectionHeader}>
        {title}
      </Typography>
      {bullets}
      <Typography variant="body1">{footnote}</Typography>
    </Grid>
  );
};

const NotesForAASection = () => {
  const { t } = useTranslation();

  const { classes } = useStyles();

  const SubmissionStepTwoDesc = (
    <Typography>
      {t('accessArrangement->>Notes.SubmissionStepTwoDesc1')}
      <br />
      <br />
      {t('accessArrangement->>Notes.SubmissionStepTwoDesc2')}
      <br />
      <br />
      {t('accessArrangement->>Notes.SubmissionStepTwoDesc3')}
    </Typography>
  );

  const SubmissionStepThreeDesc = (
    <Typography>
      <HtmlTrans i18nKey="accessArrangement->>Notes.SubmissionStepThreeDesc1" />
    </Typography>
  );

  const SubmissionStepFourDesc = (
    <Typography>
      <HtmlTrans i18nKey="accessArrangement->>Notes.SubmissionStepFourDesc1" />
      <br />
      {t('accessArrangement->>Notes.SubmissionStepFourDesc2')}
      <br />
      {t('accessArrangement->>Notes.SubmissionStepFourDesc3')}
      <br />
      <br />
      <HtmlTrans
        i18nKey="accessArrangement->>Notes.SubmissionStepFourDesc4"
        values={{
          aClass: classes.a,
        }}
      />
    </Typography>
  );

  return (
    <>
      <SectionHeader title={t('accessArrangement->>Notes.NotesForAccessApplicationHeader')} />
      <Section title={t('accessArrangement->>Notes.SubmissionDeadlineTitle')}>
        <Typography variant="body1">
          {t('accessArrangement->>Notes.SubmissionDeadlineDesc')}
        </Typography>
      </Section>
      <Section title={t('accessArrangement->>Notes.SubmissionStepTitle')}>
        <SubmissionStepSubSection
          title={t('accessArrangement->>Notes.SubmissionStepOneTitle')}
          description={t('accessArrangement->>Notes.SubmissionStepOneDesc')}
        />
        <SubmissionStepSubSection
          title={t('accessArrangement->>Notes.SubmissionStepTwoTitle')}
          description={SubmissionStepTwoDesc}
        />
        <SubmissionStepSubSection
          title={t('accessArrangement->>Notes.SubmissionStepThreeTitle')}
          description={SubmissionStepThreeDesc}
        />
        <SubmissionStepSubSection
          title={t('accessArrangement->>Notes.SubmissionStepFourTitle')}
          description={SubmissionStepFourDesc}
        />
      </Section>
      <Grid className={classes.learningDisablilitiesBulletSectionContainer}>
        <Typography variant="h3" className={classes.sectionTitle}>
          <HtmlTrans
            i18nKey="accessArrangement->>Notes.DocumentsHeaderTitle"
            values={{
              ulClass: classes.a,
            }}
          />
        </Typography>
      </Grid>
    </>
  );
};

export default NotesForAASection;
